import { memo, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { useImageRetry } from '@/hooks/ui/useImageRetry'

/* ─── Grid Shell ─── */

function ItemGrid({ children, variant = 'default' }: { children: ReactNode; variant?: 'default' | 'compact' }) {
  const gridClass =
    variant === 'compact'
      ? 'grid grid-cols-3 gap-3 overflow-hidden'
      : 'grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 overflow-hidden'

  return <div className={gridClass}>{children}</div>
}

/* ─── Item wrapper ─── */

const Item = memo(function Item({
  to,
  onClick,
  children,
}: {
  to: string
  onClick?: (e: React.MouseEvent) => void
  children: ReactNode
}) {
  return (
    <Link to={to} className="group min-w-0" onClick={onClick} data-testid="card-item">
      <div className="card p-2 transition-transform group-hover:scale-105 h-fit min-w-0">{children}</div>
    </Link>
  )
})

/* ─── Image container ─── */

const Image = memo(function Image({
  src,
  alt,
  objectFit = 'cover',
  children,
}: {
  src?: string | null
  alt: string
  objectFit?: 'cover' | 'contain'
  children?: ReactNode
}) {
  const { src: imgSrc, onError, onLoad, failed } = useImageRetry(src)
  const fitClass = objectFit === 'contain' ? 'object-contain' : 'object-cover'

  return (
    <div className="relative bg-well rounded overflow-hidden">
      {imgSrc && !failed ? (
        <img
          src={imgSrc}
          alt={alt}
          className={`block w-full max-w-full aspect-[3/4] ${fitClass}`}
          loading="lazy"
          onError={onError}
          onLoad={onLoad}
        />
      ) : (
        <div className="flex items-center justify-center aspect-[3/4] text-faint">No Image</div>
      )}
      {children}
    </div>
  )
})

/* ─── Badge group (top-left overlay) ─── */

function Badges({ children }: { children: ReactNode }) {
  return <div className="absolute top-[8%] left-[4%] flex flex-col gap-1">{children}</div>
}

function Badge({ color, children }: { color: string; children: ReactNode }) {
  return (
    <div
      className={`px-[0.4em] py-[0.15em] ${color} rounded-full text-white text-[clamp(.8rem,1.8vw,1.7rem)] font-bold shadow-lg leading-tight`}
    >
      {children}
    </div>
  )
}

/* ─── Hover actions (top-right overlay, desktop only) ─── */

function HoverActions({ children }: { children: ReactNode }) {
  return (
    <div className="absolute top-[4%] right-[4%] hidden md:flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
      {children}
    </div>
  )
}

function HoverButton({
  onClick,
  disabled,
  title,
  className = '',
  children,
}: {
  onClick: (e: React.MouseEvent) => void
  disabled?: boolean
  title: string
  className?: string
  children: ReactNode
}) {
  return (
    <button
      type="button"
      onClick={(e) => {
        e.preventDefault()
        e.stopPropagation()
        onClick(e)
      }}
      disabled={disabled}
      className={`p-[0.4em] bg-black/60 rounded-full transition-colors text-[clamp(0.65rem,2vw,1rem)] ${className}`}
      title={title}
      aria-label={title}
    >
      {children}
    </button>
  )
}

/* ─── Mobile actions (bottom-right overlay, always visible) ─── */

function MobileActions({ children }: { children: ReactNode }) {
  return <div className="absolute bottom-[4%] right-[4%] flex md:hidden flex-col gap-1.5">{children}</div>
}

function MobileButton({
  onClick,
  disabled,
  label,
  className = '',
  children,
}: {
  onClick: (e: React.MouseEvent) => void
  disabled?: boolean
  label?: string
  className?: string
  children: ReactNode
}) {
  return (
    <button
      type="button"
      onClick={(e) => {
        e.preventDefault()
        e.stopPropagation()
        onClick(e)
      }}
      disabled={disabled}
      className={`p-2.5 bg-black/60 rounded-full text-white ${className}`}
      aria-label={label}
    >
      {children}
    </button>
  )
}

/* ─── Info section (below image) ─── */

function Info({ children }: { children: ReactNode }) {
  return <div className="mt-2">{children}</div>
}

/* ─── Info sub-elements ─── */

function InfoTitle({ children }: { children: ReactNode }) {
  return <h3 className="text-sm font-medium text-primary truncate">{children}</h3>
}

function InfoSubtitle({ children }: { children: ReactNode }) {
  return <p className="text-xs text-muted">{children}</p>
}

function InfoBadges({ children }: { children: ReactNode }) {
  return <div className="flex items-center gap-1 mt-1 flex-wrap">{children}</div>
}

function InfoBadge({ color = 'bg-subtle-bg', children }: { color?: string; children: ReactNode }) {
  return <span className={`${color} text-white text-xs px-1.5 py-0.5 rounded`}>{children}</span>
}

/* ─── Empty state ─── */

function Empty({ message }: { message: string }) {
  return (
    <div className="text-center py-12">
      <p className="text-muted">{message}</p>
    </div>
  )
}

/* ─── Attach sub-components ─── */

ItemGrid.Item = Item
ItemGrid.Image = Image
ItemGrid.Badges = Badges
ItemGrid.Badge = Badge
ItemGrid.HoverActions = HoverActions
ItemGrid.HoverButton = HoverButton
ItemGrid.MobileActions = MobileActions
ItemGrid.MobileButton = MobileButton
ItemGrid.Info = Info
ItemGrid.InfoTitle = InfoTitle
ItemGrid.InfoSubtitle = InfoSubtitle
ItemGrid.InfoBadges = InfoBadges
ItemGrid.InfoBadge = InfoBadge
ItemGrid.Empty = Empty

export { ItemGrid }
